/**
 * Commission hooks — TanStack Query wrappers for the commission-rules and commissions API.
 *
 * Hooks:
 * Commission Rules (admin only):
 * - useCommissionRules(filters?)      — list commission rules
 * - useCreateCommissionRule()         — create a new rule (super_admin)
 * - useUpdateCommissionRule()         — update an existing rule (super_admin)
 *
 * Commission Entries:
 * - useCommissionEntries(filters?)    — list entries (admin=all, reseller/freelance=own)
 * - useApproveCommission()            — transition pending → approved (admin)
 * - usePayCommission()                — transition approved → paid (admin)
 * - useRejectCommission()             — transition pending → rejected (admin)
 *
 * Note: the API returns snake_case rows matching the DB schema.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '@/lib/api'

// ── Row types (snake_case — mirrors API response) ──────────────────────────────

export interface CommissionRuleRow {
  id: string
  name: string
  channel: 'reseller' | 'freelance'
  rate_basis_points: number
  applies_to: string
  min_amount_cents: number
  max_amount_cents: number | null
  active: boolean
  created_at: string
  updated_at: string
}

export interface CommissionEntryRow {
  id: string
  user_id: string
  invoice_id: string
  rule_id: string
  amount_cents: number
  status: 'pending' | 'approved' | 'paid' | 'rejected'
  approved_at: string | null
  paid_at: string | null
  notes: string | null
  created_at: string
  /** Joined from users */
  user_name: string | null
  user_email: string | null
  /** Joined from invoices */
  invoice_amount_cents: number
  invoice_status: string
}

// ── Filter types ───────────────────────────────────────────────────────────────

export interface CommissionRuleFilters {
  channel?: 'reseller' | 'freelance'
  active?: boolean
}

export interface CommissionEntryFilters {
  status?: 'pending' | 'approved' | 'paid' | 'rejected'
  userId?: string
  limit?: number
  offset?: number
}

// ── DTO types ──────────────────────────────────────────────────────────────────

export interface CreateCommissionRuleDTO {
  name: string
  channel: 'reseller' | 'freelance'
  rateBasisPoints: number
  appliesTo?: string
  minAmountCents?: number
  maxAmountCents?: number | null
  active?: boolean
}

export interface UpdateCommissionRuleDTO {
  name?: string
  rateBasisPoints?: number
  appliesTo?: string
  minAmountCents?: number
  maxAmountCents?: number | null
  active?: boolean
}

// ── Response wrappers ─────────────────────────────────────────────────────────

interface CommissionRulesListApiResponse {
  data: CommissionRuleRow[]
}

interface CommissionRuleDetailApiResponse {
  data: CommissionRuleRow
}

interface CommissionEntriesListApiResponse {
  data: CommissionEntryRow[]
}

interface CommissionEntryDetailApiResponse {
  data: CommissionEntryRow
}

// ── Query key factory ──────────────────────────────────────────────────────────

export const commissionKeys = {
  rules: {
    all: ['commission-rules'] as const,
    list: (filters?: CommissionRuleFilters) =>
      [...commissionKeys.rules.all, 'list', filters] as const,
  },
  entries: {
    all: ['commissions'] as const,
    list: (filters?: CommissionEntryFilters) =>
      [...commissionKeys.entries.all, 'list', filters] as const,
  },
}

// ── Commission Rule hooks ──────────────────────────────────────────────────────

/**
 * Fetch all commission rules (admin only).
 * Supports optional channel and active filters.
 */
export function useCommissionRules(filters?: CommissionRuleFilters) {
  const params = new URLSearchParams()
  if (filters?.channel) params.set('channel', filters.channel)
  if (filters?.active !== undefined) params.set('active', String(filters.active))
  const qs = params.toString()
  const path = `/api/v1/commission-rules${qs ? `?${qs}` : ''}`

  return useQuery<CommissionRuleRow[]>({
    queryKey: commissionKeys.rules.list(filters),
    queryFn: async () => {
      const res = await apiGet<CommissionRulesListApiResponse>(path)
      return res.data
    },
  })
}

/**
 * Mutation to create a commission rule (super_admin only).
 * Invalidates the rules list cache on success.
 */
export function useCreateCommissionRule() {
  const queryClient = useQueryClient()

  return useMutation<CommissionRuleRow, Error, CreateCommissionRuleDTO>({
    mutationFn: async (data) => {
      const res = await apiPost<CommissionRuleDetailApiResponse>('/api/v1/commission-rules', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.rules.all })
    },
  })
}

/**
 * Mutation to update a commission rule (super_admin only).
 * Invalidates the rules list cache on success.
 */
export function useUpdateCommissionRule() {
  const queryClient = useQueryClient()

  return useMutation<CommissionRuleRow, Error, { id: string; data: UpdateCommissionRuleDTO }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPut<CommissionRuleDetailApiResponse>(
        `/api/v1/commission-rules/${id}`,
        data,
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.rules.all })
    },
  })
}

// ── Commission Entry hooks ────────────────────────────────────────────────────

/**
 * Fetch commission entries with optional filters.
 * Admin sees all entries; reseller/freelance see only their own.
 */
export function useCommissionEntries(filters?: CommissionEntryFilters) {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.userId) params.set('userId', filters.userId)
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const path = `/api/v1/commissions${qs ? `?${qs}` : ''}`

  return useQuery<CommissionEntryRow[]>({
    queryKey: commissionKeys.entries.list(filters),
    queryFn: async () => {
      const res = await apiGet<CommissionEntriesListApiResponse>(path)
      return res.data
    },
  })
}

/**
 * Mutation to approve a pending commission entry (admin only).
 * Transitions: pending → approved.
 * Invalidates the entries list cache on success.
 */
export function useApproveCommission() {
  const queryClient = useQueryClient()

  return useMutation<CommissionEntryRow, Error, { id: string; notes?: string }>({
    mutationFn: async ({ id, notes }) => {
      const res = await apiPost<CommissionEntryDetailApiResponse>(
        `/api/v1/commissions/${id}/approve`,
        { notes },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.entries.all })
    },
  })
}

/**
 * Mutation to mark an approved commission entry as paid (admin only).
 * Transitions: approved → paid.
 * Invalidates the entries list cache on success.
 */
export function usePayCommission() {
  const queryClient = useQueryClient()

  return useMutation<CommissionEntryRow, Error, { id: string; notes?: string }>({
    mutationFn: async ({ id, notes }) => {
      const res = await apiPost<CommissionEntryDetailApiResponse>(
        `/api/v1/commissions/${id}/pay`,
        { notes },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.entries.all })
    },
  })
}

/**
 * Mutation to reject a pending commission entry (admin only).
 * Transitions: pending → rejected.
 * Invalidates the entries list cache on success.
 */
export function useRejectCommission() {
  const queryClient = useQueryClient()

  return useMutation<CommissionEntryRow, Error, { id: string; reason: string }>({
    mutationFn: async ({ id, reason }) => {
      const res = await apiPost<CommissionEntryDetailApiResponse>(
        `/api/v1/commissions/${id}/reject`,
        { reason },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.entries.all })
    },
  })
}
